import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Link as MuiLink,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const endpoint = process.env.REACT_APP_API_BASE_URL;

const DEPARTMENT_OPTIONS = Object.freeze([
  'Other (please specify)',
  'Accounting',
  'Business Administration',
  'Computer Science',
  'Data Science',
  'Electrical Engineering',
  'Finance',
  'Human Resources',
  'Industrial Engineering',
  'Information Systems',
  'Marketing',
  'Mechanical Engineering',
  'Supply Chain Management',
]);

const formatTimestamp = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(
      decodeURIComponent(
        json
          .split('')
          .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
          .join('')
      )
    );
  } catch (err) {
    return null;
  }
};

const buildAuthHeaders = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  const authValue = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  return { Authorization: authValue };
};

const resolveIdentity = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    return { name: '', email: '', userType: '' };
  }
  const parsed = decodeJwt(token.startsWith('Bearer ') ? token.slice(7) : token) || {};

  const resolveName = (user = {}) => {
    const first =
      user.FirstName ||
      user.firstName ||
      user.given_name ||
      user.givenName ||
      '';
    const last =
      user.LastName ||
      user.lastName ||
      user.family_name ||
      user.surname ||
      '';
    const display = user.DisplayName || user.displayName || user.Name || user.name || '';
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    if (display) {
      if (display.includes(',')) {
        const parts = display
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean);
        if (parts.length === 2) {
          return `${parts[1]} ${parts[0]}`.trim();
        }
      }
      return display.trim();
    }
    const email = resolveEmail(user);
    if (!email) return '';
    const local = email.split('@')[0];
    return local
      .replace(/[._-]+/g, ' ')
      .split(' ')
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  };

  const resolveEmail = (user = {}) =>
    user.Email ||
    user.email ||
    user.mail ||
    user.userPrincipalName ||
    user.preferred_username ||
    '';

  const userType = (parsed.userType || parsed.UserType || '').toString().toLowerCase();

  return {
    name: resolveName(parsed) || '',
    email: resolveEmail(parsed) || '',
    userType,
  };
};

const Profile = () => {
  const identity = useMemo(resolveIdentity, []);
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: identity.name,
    email: identity.email,
    department: '',
    resumeUrl: '',
  });
  const [formState, setFormState] = useState({ department: '' });
  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const hasChanges = useMemo(() => {
    const deptChanged = formState.department !== (profile.department || '');
    return deptChanged || !!resumeFile;
  }, [formState, profile, resumeFile]);

  useEffect(() => {
    if (identity.userType === 'employee') {
      navigate('/dashboard', { replace: true });
    }
  }, [identity.userType, navigate]);

  useEffect(() => {
    const department = profile.department || '';
    setFormState((prev) =>
      prev.department === department ? prev : { ...prev, department }
    );
  }, [profile.department]);

  useEffect(() => {
    if (identity.userType !== 'student') {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      const headers = buildAuthHeaders();
      if (!headers) {
        setError('Session expired. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${endpoint}/api/profile`, {
          method: 'GET',
          headers,
        });
        if (!response.ok) {
          throw new Error(`Failed to load profile (${response.status})`);
        }
        const data = await response.json();
        setProfile({
          name: data.name || identity.name || data.email || identity.email,
          email: data.email || identity.email,
          department: data.department || '',
          resumeUrl: data.resumeUrl || '',
          updatedAt: data.updatedAt || null,
        });
        setFormState({ department: data.department || '' });
        setError(null);
      } catch (fetchError) {
        console.error('Profile fetch error:', fetchError);
        setProfile((prev) => ({
          ...prev,
          name: identity.name || prev.name,
          email: identity.email || prev.email,
        }));
        setError('Unable to load profile from server. You can still update your resume.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [identity.email, identity.name, identity.userType]);

  const handleResumeChange = (event) => {
    const file = event.target.files?.[0] || null;
    setResumeFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const headers = buildAuthHeaders();
    if (!headers) {
      setError('Session expired. Please log in again.');
      return;
    }

    const formData = new FormData();
    formData.append('department', formState.department || '');
    if (resumeFile) {
      formData.append('Student_Resume', resumeFile);
    }

    setSaving(true);
    setMessage(null);
    setError(null);

    try {
      const response = await fetch(`${endpoint}/api/profile`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to update profile');
      }

      const updated = await response.json();
      setProfile((prev) => ({ ...prev, ...updated }));
      setFormState({ department: updated.department || '' });
      setResumeFile(null);
      setMessage('Profile saved & submitted.');
    } catch (submitError) {
      console.error('Profile update error:', submitError);
      setError('Failed to update your profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="50vh">
        <CircularProgress thickness={5} />
      </Box>
    );
  }

  const displayName = profile.name || identity.name || 'Student Name';
  const displayEmail = profile.email || identity.email || 'email@tamu.edu';
  const lastUpdated = formatTimestamp(profile.updatedAt);
  if (identity.userType === 'employee') {
    return null;
  }

  if (identity.userType !== 'student') {
    return (
      <Box maxWidth="720px" margin="0 auto" padding={{ xs: 2, sm: 4 }}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            padding: { xs: 3, sm: 4 },
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Profile
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Profile management is only available for student accounts.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box maxWidth="960px" margin="0 auto" padding={{ xs: 2, sm: 4 }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          border: '1px solid rgba(80,0,0,0.12)',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #500000 0%, #320000 100%)',
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 5 },
          }}
        >
          <Typography variant="h3" fontWeight={700} gutterBottom color="#ffffff">
            Profile
          </Typography>
          <Typography variant="h5" fontWeight={600} sx={{ opacity: 0.95 }} color="#ffffff">
            {displayName}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.75 }} color="#ffffff">
            {displayEmail}
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 3, sm: 5 }, py: { xs: 3, sm: 4 } }}>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Upload your resume once and it will be attached automatically to every future proposal. You can update it here anytime.
          </Typography>

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}

              <Autocomplete
                freeSolo
                options={DEPARTMENT_OPTIONS}
                value={formState.department || ''}
                onChange={(_event, value) =>
                  setFormState((prev) => ({ ...prev, department: value || '' }))
                }
                onInputChange={(_event, value) =>
                  setFormState((prev) => ({ ...prev, department: value || '' }))
                }
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    placeholder="Select or type a department"
                    inputProps={{
                      ...params.inputProps,
                      style: { ...(params.inputProps?.style || {}), cursor: 'text' },
                    }}
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    cursor: 'text',
                  },
                  '& .MuiOutlinedInput-input': {
                    cursor: 'text',
                  },
                }}
              />

              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Resume
                </Typography>
                {profile.resumeUrl ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Current file:{' '}
                    <MuiLink href={profile.resumeUrl} target="_blank" rel="noopener noreferrer" underline="hover" color="#500000">
                      View Resume
                    </MuiLink>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                    No resume uploaded yet.
                  </Typography>
                )}

                <Button
                  variant="contained"
                  component="label"
                  sx={{ backgroundColor: '#500000', '&:hover': { backgroundColor: '#3c0000' }, px: 3 }}
                >
                  {resumeFile ? 'Change Resume' : 'Upload Resume'}
                  <input type="file" hidden accept="application/pdf" onChange={handleResumeChange} />
                </Button>
                {resumeFile && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Selected: {resumeFile.name}
                  </Typography>
                )}
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || !hasChanges}
                  sx={{
                    backgroundColor: '#500000',
                    '&:hover': { backgroundColor: '#3c0000' },
                    px: 4,
                    py: 1.25,
                    fontWeight: 600,
                  }}
                >
                  {saving ? 'Saving…' : 'Save & Submit'}
                </Button>
              </Box>

              {message && (
                <Alert severity="success">
                  {message}
                  {lastUpdated ? ` (Last updated ${lastUpdated})` : ''}
                </Alert>
              )}

              {!message && lastUpdated && (
                <Typography variant="caption" color="textSecondary">
                  Last updated {lastUpdated}.
                </Typography>
              )}
            </Stack>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default Profile;
